
import { useCallback, useEffect, useRef } from 'react';
import { useTransactions } from '@/contexts/transactions';
import { useBudgets } from '@/contexts/budgets';
import { useFutureReleases } from '@/contexts/future-releases';
import { startOfMonth, endOfMonth, startOfYear, endOfYear, startOfWeek, endOfWeek, startOfDay, endOfDay } from 'date-fns';
import { createHistorySnapshot } from '@/lib/supabase-budget-history-operations';
import { supabase } from '@/integrations/supabase/client';
import { dateToLocalString, localStringToDate } from '@/utils/dateFormatUtils';
import { calculatePeriodSpent, getLastPeriodDates } from '@/utils/budgetHistoryUtils';
import { logger } from '@/utils/logger';

interface UseBudgetSyncOptions {
  includePending?: boolean;
}

export const useBudgetSync = (options: UseBudgetSyncOptions = {}) => {
  const { includePending = false } = options;
  const { transactions, isLoading } = useTransactions();
  const { budgets, updateBudget } = useBudgets();
  const { futureReleases } = useFutureReleases();
  
  // Rastrear históricos já criados nesta sessão
  const createdHistoriesRef = useRef<Set<string>>(new Set());

  const calculateBudgetSpent = useCallback((budgetId: string, period: string, categoryId?: string, includeAllPending: boolean = false) => {
    const now = new Date();
    let startDate: Date;
    let endDate: Date;

    // Determinar o período baseado no tipo
    switch (period) {
      case 'diario':
        startDate = startOfDay(now);
        endDate = endOfDay(now);
        break;
      case 'semanal':
        startDate = startOfWeek(now, { weekStartsOn: 1 }); // Segunda-feira
        endDate = endOfWeek(now, { weekStartsOn: 1 });
        break;
      case 'mensal':
        startDate = startOfMonth(now);
        endDate = endOfMonth(now);
        break;
      case 'anual':
        startDate = startOfYear(now);
        endDate = endOfYear(now);
        break;
      default:
        return 0;
    }

    // Filtrar transações do período e categoria
    const relevantTransactions = transactions.filter(transaction => {
      const transactionDate = localStringToDate(transaction.date);
      const isInPeriod = transactionDate >= startDate && transactionDate <= endDate;
      const isExpense = transaction.type === 'despesa';
      const statusVal = String(transaction.status);
      
      // Se includeAllPending, incluir também pendentes
      const isEffective = includeAllPending 
        ? (statusVal === 'paid' || statusVal === 'efetivada' || statusVal === 'partial' || statusVal === 'pending' || statusVal === 'pendente')
        : (statusVal === 'paid' || statusVal === 'efetivada' || statusVal === 'partial');
      
      const matchesCategory = !categoryId || transaction.categoryId === categoryId;
      const isNotDeleted = !transaction.deleted;

      return isInPeriod && isExpense && isEffective && matchesCategory && isNotDeleted;
    });

    // Calcular total gasto das transações
    let totalSpent = relevantTransactions.reduce((sum, transaction) => {
      return sum + Math.abs(transaction.amount);
    }, 0);

    // Se includeAllPending, adicionar também lançamentos futuros do período
    if (includeAllPending && futureReleases) {
      const relevantFutureReleases = futureReleases.filter(release => {
        const releaseDate = localStringToDate(release.dueDate);
        const isInPeriod = releaseDate >= startDate && releaseDate <= endDate;
        const isExpense = release.type === 'despesa';
        const matchesCategory = !categoryId || release.categoryId === categoryId;
        // Check for pending statuses that exist in the type
        const isPending = release.status === 'agendado' || release.status === 'ativo';

        return isInPeriod && isExpense && matchesCategory && isPending;
      });

      const futureReleasesTotal = relevantFutureReleases.reduce((sum, release) => {
        return sum + Math.abs(release.amount);
      }, 0);

      totalSpent += futureReleasesTotal;
    }

    logger.debug(`Budget ${budgetId} - Period: ${period}, Spent: ${totalSpent}, IncludePending: ${includeAllPending}`);
    return totalSpent;
  }, [transactions, futureReleases]);

  // Sincronizar gastos dos orçamentos automaticamente
  useEffect(() => {
    const syncBudgets = async () => {
      // Aguardar carregamento completo das transações (descriptografadas)
      if (isLoading) {
        logger.info('[BudgetSync] Waiting for transactions to load and decrypt...');
        return;
      }

      if (!budgets || budgets.length === 0) return;

      const user = await supabase.auth.getUser();
      const userId = user.data.user?.id;
      if (!userId) return;

      logger.info(`[BudgetSync] Starting sync with ${transactions.length} decrypted transactions`);

      for (const budget of budgets) {
        const spent = calculateBudgetSpent(budget.id, budget.period, budget.categoryId);
        
        // Atualizar apenas se o valor mudou
        if (Math.abs(spent - (budget.spent || 0)) > 0.01) {
          logger.info(`[BudgetSync] Updating budget ${budget.name}: spent ${budget.spent} -> ${spent}`);
          await updateBudget(budget.id, { spent });
        }

        // Sempre processar o último período encerrado para capturar transações retroativas
        const { startDate, endDate } = getLastPeriodDates(budget.period, new Date());
        const historyKey = `${budget.id}-${startDate}-${endDate}`;

        // Verificar se já foi processado nesta sessão
        if (!createdHistoriesRef.current.has(historyKey)) {
          logger.info(`[SYNC] Processing last closed period for budget ${budget.name} (${budget.period}): ${startDate} to ${endDate}`);
          
          // Calcular o gasto do período encerrado
          const spentForRange = calculatePeriodSpent(
            transactions, 
            startDate, 
            endDate, 
            budget.categoryId
          );
          
          logger.info(`[SYNC] Calculated spent for closed period: ${spentForRange}`);

          // Validar se há gastos ou orçamento antes de criar snapshot (evita registros vazios)
          if (spentForRange > 0 || budget.amount > 0) {
            logger.info(`[SYNC] Creating/updating snapshot: ${budget.name} | Period: ${startDate} to ${endDate} | Spent: ${spentForRange} | Budgeted: ${budget.amount}`);
            
            // Criar/atualizar snapshot do histórico (com upsert)
            const result = await createHistorySnapshot(
              budget.id,
              userId,
              undefined,
              budget.period || 'mensal',
              budget.amount,
              spentForRange,
              startDate,
              endDate
            );

            if (result.success) {
              logger.info(`[SYNC] Successfully upserted history snapshot for ${budget.name}`);
              createdHistoriesRef.current.add(historyKey);
            }
          } else {
            logger.info(`[SYNC] Skipping snapshot creation for ${budget.name} - no spent and no budget`);
          }
        }
      }
    };

    // Limpar cache de históricos criados quando transações mudam (forçar recálculo)
    createdHistoriesRef.current.clear();
    
    syncBudgets();
    
    // Sincronizar a cada 5 minutos para detectar mudanças
    const interval = setInterval(syncBudgets, 300000);
    return () => clearInterval(interval);
  }, [budgets, transactions, calculateBudgetSpent, isLoading, updateBudget]);

  const getBudgetPeriodDates = (period: string, referenceDate: Date): { startDate: string; endDate: string } => {
    let startDate: Date;
    let endDate: Date;

    switch (period) {
      case 'diario':
        startDate = startOfDay(referenceDate);
        endDate = endOfDay(referenceDate);
        break;
      case 'semanal':
        startDate = startOfWeek(referenceDate, { weekStartsOn: 1 });
        endDate = endOfWeek(referenceDate, { weekStartsOn: 1 });
        break;
      case 'mensal':
        startDate = startOfMonth(referenceDate);
        endDate = endOfMonth(referenceDate);
        break;
      case 'anual':
        startDate = startOfYear(referenceDate);
        endDate = endOfYear(referenceDate);
        break;
      default: {
        const d = dateToLocalString(new Date());
        return { startDate: d, endDate: d };
      }
    }

    return {
      startDate: dateToLocalString(startDate),
      endDate: dateToLocalString(endDate)
    };
  };

  return {
    calculateBudgetSpent
  };
};
